import { Component } from '@theme/component';

/**
 * Arrow controls for a horizontally-scrolling rail.
 *
 * The rail scrolls natively (and snaps) on touch; this only adds pointer affordances and keeps
 * them disabled at the ends so they never look live when they aren't.
 *
 * @extends {Component<{
 *   rail: HTMLElement,
 *   previous: HTMLButtonElement,
 *   next: HTMLButtonElement,
 * }>}
 */
class OudhCarousel extends Component {
  #raf = 0;
  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  connectedCallback() {
    super.connectedCallback();

    const { rail } = this.refs;
    if (!rail) return;

    rail.addEventListener('scroll', this.#onScroll, { passive: true });
    this.#resizeObserver = new ResizeObserver(this.#onScroll);
    this.#resizeObserver.observe(rail);
    this.#updateButtons();
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.refs.rail?.removeEventListener('scroll', this.#onScroll);
    this.#resizeObserver?.disconnect();
  }

  #onScroll = () => {
    if (this.#raf) return;
    this.#raf = requestAnimationFrame(() => {
      this.#raf = 0;
      this.#updateButtons();
    });
  };

  #updateButtons() {
    const { rail, previous, next } = this.refs;
    if (!rail) return;

    // A pixel of slack absorbs sub-pixel scroll positions that would otherwise leave the
    // end-of-rail button enabled.
    const atStart = rail.scrollLeft <= 1;
    const atEnd = rail.scrollLeft >= rail.scrollWidth - rail.clientWidth - 1;

    previous?.toggleAttribute('disabled', atStart);
    next?.toggleAttribute('disabled', atEnd);
  }

  /** Scrolls by one viewport, less a card's worth of overlap for context. */
  #step() {
    const { rail } = this.refs;
    if (!rail) return 0;

    const firstChild = rail.firstElementChild;
    const cardWidth = firstChild instanceof HTMLElement ? firstChild.offsetWidth : 0;
    return Math.max(cardWidth, rail.clientWidth - cardWidth) || rail.clientWidth;
  }

  next() {
    this.refs.rail?.scrollBy({ left: this.#step(), behavior: 'smooth' });
  }

  previous() {
    this.refs.rail?.scrollBy({ left: -this.#step(), behavior: 'smooth' });
  }
}

if (!customElements.get('oudh-carousel')) {
  customElements.define('oudh-carousel', OudhCarousel);
}
